import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Icon from "../icon/Icon";
import Button from "../button/Button";
import type { InputNumberProps } from "./InputNumber.types";
import "./inputNumber.scss";

const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(({
  label,
  iconName,
  step,
  disabled,
  error,
  errorMessage,
  placeholder,
  clear,
  signs,
  maxScore,
  inputText,
  minVal,
  maxVal,
  onChange,
  value,
  name,
}, ref ) => {
  const [content, setContent] = useState(value ?? "");
  const newV = ((typeof value === "string") ? parseInt(value) : value) 
  const newS = newV ?? step;
  const [score, setScore] = useState(newS);
  const [exceeded, setExceeded] = useState(false);

  const isClear = clear ? "sb-inputNumber--clear" : "";
  const isDisabled = disabled
    ? "sb-inputNumber--disabled"
    : "sb-inputNumber--enabled";
  const isError = error ? "sb-inputNumber--error" : "";

  useEffect(() => {
    if (value !== undefined) {
      setContent(value);
    }
  }, [value]);

  const handleIsExceeded = (total: number) => {
    const maxScoreres = maxScore ?? 0;

    if (total >= maxScoreres) {
      setExceeded(true);
    } else if (total <= maxScoreres) {
      setExceeded(false);
    }
  };

  const increaseScoreQuestion = (score: number) => {
    const maxScoreResponse = maxScore;
    if (score === maxScoreResponse) return;
    const intervalScore = step;
    const total = step ? score + step : 0;

    handleIsExceeded(total);

    if (
      step &&
      maxScoreResponse &&
      intervalScore &&
      score + step < maxScoreResponse
    ) {
      setScore(score + intervalScore);
    } else {
      setScore(maxScoreResponse || 20);
    }
  };

  const reduceScoreQuestion = (score: number) => {
    const minScore = step;
    if (score === minScore) return;
    const intervalScore = step;
    const total = step ? score - step : 0;
    handleIsExceeded(total);

    if (minScore && intervalScore && score > minScore) {
      setScore(score - intervalScore);
    }
  };

  return (
    <div
      className={["sb-inputNumber", isDisabled, isError, isClear].join(" ")}
      data-testid="sb-inputNumber"
    >
      {label && (
        <div className="sb-inputNumber__label flex mb-xsm">
          {iconName && (
            <Icon iconName={iconName} size={13} className="mr-xsm" />
          )}
          <span>{label}</span>
        </div>
      )}
      {signs ? (
        <div
          className="sb-inputNumber__signs"
          data-testid="sb-inputNumber__signs"
        >
          <Button
            color="primary"
            type="clear"
            size="md"
            iconName="minus"
            onClick={() => !disabled && score && reduceScoreQuestion(score)}
            style={{ padding: "0" }}
            disabled={(step && score && score <= step) || disabled}
          />
          <div className="relative text-center">
            <span
              className={`font-bold text-center ${
                disabled ? "text-disabled" : "text-neutral-03"
              }`}
            >
              {score} {inputText}
            </span>
          </div>
          <Button
            color="primary"
            type="clear"
            size="md"
            iconName="plus"
            onClick={() =>
              !exceeded && !disabled && score && increaseScoreQuestion(score)
            }
            style={{ padding: "0" }}
            disabled={exceeded || disabled}
          />
        </div>
      ) : (
        <input
          className="sb-inputNumber__container"
          data-testid="sb-inputNumber__container"
          onChange={(e: any) => {
            const num = Number(e.target.value);
            if (!isNaN(num)) {
              if (minVal !== undefined && maxVal !== undefined) {
                if (minVal <= num && maxVal >= num) {
                  setContent(e.target.value);
                  onChange && onChange(e);
                }
              } else {
                setContent(e.target.value);
                onChange && onChange(e);
              }
            }
          }}
          value={content}
          placeholder={placeholder}
          disabled={disabled}
          type={"number"}
          min={minVal}
          max={maxVal}
          name={name}
          ref={ref}
        />
      )}
      <div
        className={`flex mt-xsm text-small-02 ${
          error ? "justify-between" : "justify-end"
        }`}
      >
        {error && (
          <p className="flex text-error mr-sm">
            <Icon
              iconName="alert-triangle"
              size={13}
              className="text-error mr-xsm"
            />
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
});

InputNumber.propTypes = {
  /**
   * label
   */
  label: PropTypes.string,
  /**
   * iconName
   */
  iconName: PropTypes.string,
  /**
   * placeholder
   */
  placeholder: PropTypes.string,
  /**
   * step
   */
  step: PropTypes.number,
  /**
   * maxScore
   */
  maxScore: PropTypes.number,
  /**
   * is clear?
   */
  clear: PropTypes.bool,
  /**
   *  signs?
   */
  signs: PropTypes.bool,
  /**
   *  inputText
   */
  inputText: PropTypes.string,
  /**
   * disabled?
   */
  disabled: PropTypes.bool,
  /**
   * Has error?
   */
  error: PropTypes.bool,
  /**
   * errorMessage
   */
  errorMessage: PropTypes.string,
  /**
   * minVal
   */
  minVal: PropTypes.number,
  /**
   * maxVal
   */
  maxVal: PropTypes.number,
  /**
   * onChange
   */
  onChange: PropTypes.func,
  /**
   * value
   */
  value: PropTypes.string,

  /** name
   */
  name: PropTypes.string,
  /**
   * ref
   */
  ref: PropTypes.any,
};

InputNumber.defaultProps = {
  label: "",
  iconName: "check",
  placeholder: "",
  disabled: false,
  error: false,
  errorMessage: "Error",
  clear: false,
  signs: false,
  maxScore: 20,
  inputText: "pts",
  step: 0.5,
  minVal: 0,
  maxVal: 20,
  onChange: () => {},
  value: "",
  name:"",
  ref:null
};

export default InputNumber;
